import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  IconButton,
  Typography,
  Paper,
  Avatar,
  Menu,
  MenuItem,
  Alert,
  CircularProgress,
} from "@mui/material";
import {
  Send as SendIcon,
  MoreVert as MoreVertIcon,
  Refresh as RefreshIcon,
  ExitToApp as ExitIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Remove as MinimizeIcon,
} from "@mui/icons-material";
import { ChatMessage, UserInfo } from "../hooks/useSuperQnaChat";

interface ChatInterfaceProps {
  messages: ChatMessage[];
  userInfo: UserInfo | null;
  isLoading: boolean;
  error: string | null;
  onSendMessage: (message: string) => Promise<boolean>;
  onResetChat: () => Promise<boolean>;
  onEndChat: () => void;
  onClearError: () => void;
  onMinimize?: () => void;
}

/**
 * 채팅 인터페이스 컴포넌트
 */
export const ChatInterface: React.FC<ChatInterfaceProps> = ({
  messages,
  userInfo,
  isLoading,
  error,
  onSendMessage,
  onResetChat,
  onEndChat,
  onClearError,
  onMinimize,
}) => {
  const [inputMessage, setInputMessage] = useState("");
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 메시지가 추가될 때마다 스크롤을 맨 아래로
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // 컴포넌트 마운트 시 입력 필드에 포커스
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // 메시지 전송 핸들러
  const handleSendMessage = async () => {
    const message = inputMessage.trim();
    if (!message || isLoading) return;

    setInputMessage("");
    const success = await onSendMessage(message);

    // 전송 실패 시 메시지 복원
    if (!success) {
      setInputMessage(message);
    }

    // 입력 필드에 다시 포커스
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Enter 키 핸들러
  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  // 메뉴 핸들러
  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setMenuAnchor(event.currentTarget);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleResetChat = async () => {
    handleMenuClose();
    await onResetChat();
  };

  const handleEndChat = () => {
    handleMenuClose();
    onEndChat();
  };

  // 메시지 시간 포맷팅
  const formatMessageTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString("ko-KR", {
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#f8fafc",
        borderRadius: 3,
        overflow: "hidden",
        border: "1px solid #e2e8f0",
      }}
    >
      {/* 헤더 */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          p: 3,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          position: "relative",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(10px)",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            position: "relative",
            zIndex: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: "50%",
              backgroundColor: "rgba(255,255,255,0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "2px solid rgba(255,255,255,0.3)",
            }}
          >
            <BotIcon sx={{ fontSize: "1.2rem" }} />
          </Box>
          <Box>
            <Typography
              variant="h6"
              sx={{ fontSize: "1.1rem", fontWeight: "bold", mb: 0.5 }}
            >
              WORLD-TKDLAB Chat Bot
            </Typography>
            {userInfo && (
              <Typography
                variant="caption"
                sx={{ opacity: 0.9, fontSize: "0.875rem" }}
              >
                {userInfo.userName}님과 대화 중
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, position: "relative", zIndex: 1 }}>
          {/* 최소화 버튼 */}
          {onMinimize && (
            <IconButton
              onClick={onMinimize}
              sx={{
                color: "#fbbf24", // 노란색
                backgroundColor: "rgba(251, 191, 36, 0.1)", // 노란색 배경
                "&:hover": {
                  backgroundColor: "rgba(251, 191, 36, 0.2)",
                  color: "#f59e0b", // 호버 시 더 진한 노란색
                },
              }}
              size="small"
              title="최소화"
            >
              <MinimizeIcon />
            </IconButton>
          )}

          {/* 메뉴 버튼 */}
          <IconButton
            onClick={handleMenuOpen}
            sx={{
              color: "white",
              backgroundColor: "rgba(255,255,255,0.1)",
              "&:hover": {
                backgroundColor: "rgba(255,255,255,0.2)",
              },
            }}
            size="small"
          >
            <MoreVertIcon />
          </IconButton>
        </Box>

        {/* 메뉴 */}
        <Menu
          anchorEl={menuAnchor}
          open={Boolean(menuAnchor)}
          onClose={handleMenuClose}
          anchorOrigin={{
            vertical: "bottom",
            horizontal: "right",
          }}
          transformOrigin={{
            vertical: "top",
            horizontal: "right",
          }}
        >
          <MenuItem onClick={handleResetChat}>
            <RefreshIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
            대화 초기화
          </MenuItem>
          <MenuItem onClick={handleEndChat}>
            <ExitIcon sx={{ mr: 1, fontSize: "1.2rem" }} />
            채팅 종료
          </MenuItem>
        </Menu>
      </Box>

      {/* 에러 메시지 */}
      {error && (
        <Alert
          severity="error"
          onClose={onClearError}
          sx={{
            m: 2,
            borderRadius: 2,
            backgroundColor: "#fef2f2",
            border: "1px solid #fecaca",
            "& .MuiAlert-icon": {
              color: "#dc2626",
            },
            "& .MuiAlert-message": {
              color: "#991b1b",
              fontWeight: 500,
            },
          }}
        >
          {error}
        </Alert>
      )}

      {/* 메시지 영역 */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          p: 2,
          backgroundColor: "#ffffff",
          backgroundImage:
            "radial-gradient(circle at 1px 1px, rgba(102, 126, 234, 0.05) 1px, transparent 0)",
          backgroundSize: "20px 20px",
        }}
      >
        {messages.length === 0 ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              textAlign: "center",
              color: "text.secondary",
            }}
          >
            <Typography variant="body2">채팅을 시작해보세요!</Typography>
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {messages.map((message, index) => (
              <Box
                key={`${message.messageId}-${index}`}
                sx={{
                  display: "flex",
                  justifyContent:
                    message.messageType === "USER" ? "flex-end" : "flex-start",
                  mb: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    maxWidth: "80%",
                    flexDirection:
                      message.messageType === "USER" ? "row-reverse" : "row",
                  }}
                >
                  {/* 아바타 */}
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      background:
                        message.messageType === "USER"
                          ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                          : "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
                      fontSize: "1rem",
                      border: "2px solid rgba(255,255,255,0.9)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    {message.messageType === "USER" ? (
                      <PersonIcon />
                    ) : (
                      <BotIcon />
                    )}
                  </Avatar>

                  {/* 메시지 버블 */}
                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      background:
                        message.messageType === "USER"
                          ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)"
                          : "#ffffff",
                      color:
                        message.messageType === "USER" ? "white" : "#374151",
                      borderRadius: 3,
                      borderTopLeftRadius:
                        message.messageType === "USER" ? 3 : 1,
                      borderTopRightRadius:
                        message.messageType === "USER" ? 1 : 3,
                      border:
                        message.messageType === "USER"
                          ? "none"
                          : "1px solid #e5e7eb",
                      boxShadow:
                        message.messageType === "USER"
                          ? "0 4px 15px rgba(102, 126, 234, 0.3)"
                          : "0 2px 8px rgba(0,0,0,0.08)",
                      position: "relative",
                      "&::before":
                        message.messageType === "BOT"
                          ? {
                              content: '""',
                              position: "absolute",
                              top: 0,
                              left: 0,
                              right: 0,
                              bottom: 0,
                              background:
                                "linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(74, 222, 128, 0.02) 100%)",
                              borderRadius: "inherit",
                            }
                          : {},
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        lineHeight: 1.5,
                        fontSize: "0.95rem",
                        fontWeight: message.messageType === "USER" ? 500 : 400,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {message.messageContent}
                    </Typography>

                    {/* 시간 표시 */}
                    <Typography
                      variant="caption"
                      sx={{
                        display: "block",
                        mt: 1,
                        opacity: message.messageType === "USER" ? 0.8 : 0.6,
                        fontSize: "0.75rem",
                        fontWeight: 500,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      {formatMessageTime(message.regDttm)}
                    </Typography>
                  </Paper>
                </Box>
              </Box>
            ))}

            {/* 로딩 인디케이터 */}
            {isLoading && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  mb: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Avatar
                    sx={{
                      width: 36,
                      height: 36,
                      background:
                        "linear-gradient(135deg, #4ade80 0%, #22c55e 100%)",
                      fontSize: "1rem",
                      border: "2px solid rgba(255,255,255,0.9)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                  >
                    <BotIcon />
                  </Avatar>

                  <Paper
                    elevation={0}
                    sx={{
                      p: 2,
                      backgroundColor: "#ffffff",
                      borderRadius: 3,
                      borderTopLeftRadius: 1,
                      border: "1px solid #e5e7eb",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      position: "relative",
                      "&::before": {
                        content: '""',
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background:
                          "linear-gradient(135deg, rgba(102, 126, 234, 0.02) 0%, rgba(74, 222, 128, 0.02) 100%)",
                        borderRadius: "inherit",
                      },
                    }}
                  >
                    <CircularProgress
                      size={18}
                      sx={{
                        color: "#667eea",
                        position: "relative",
                        zIndex: 1,
                      }}
                    />
                    {/* <Typography
                      variant="body2"
                      sx={{
                        color: "#6b7280",
                        fontSize: "0.95rem",
                        fontWeight: 500,
                        position: "relative",
                        zIndex: 1,
                      }}
                    >
                      
                    </Typography> */}
                  </Paper>
                </Box>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </Box>
        )}
      </Box>

      {/* 입력 영역 */}
      <Box
        sx={{
          p: 3,
          backgroundColor: "white",
          borderTop: "1px solid #e5e7eb",
          background: "linear-gradient(to top, #ffffff 0%, #f8fafc 100%)",
        }}
      >
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-end" }}>
          <TextField
            ref={inputRef}
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="메시지를 입력하세요..."
            multiline
            maxRows={3}
            disabled={isLoading}
            variant="outlined"
            fullWidth
            sx={{
              "& .MuiOutlinedInput-root": {
                backgroundColor: "white",
                borderRadius: 3,
                border: "2px solid #e5e7eb",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                fontSize: "1rem",
                "&:hover": {
                  borderColor: "#667eea",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.15)",
                },
                "&.Mui-focused": {
                  borderColor: "#667eea",
                  boxShadow:
                    "0 0 0 3px rgba(102, 126, 234, 0.1), 0 4px 12px rgba(102, 126, 234, 0.15)",
                },
                "& fieldset": {
                  border: "none",
                },
              },
              "& .MuiInputBase-input": {
                padding: "12px 16px",
                fontSize: "1rem",
                "&::placeholder": {
                  color: "#9ca3af",
                  opacity: 1,
                },
              },
            }}
          />

          <IconButton
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || isLoading}
            sx={{
              width: 48,
              height: 48,
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "white",
              boxShadow: "0 4px 15px rgba(102, 126, 234, 0.4)",
              transition: "all 0.3s ease-in-out",
              "&:hover": {
                background: "linear-gradient(135deg, #5a67d8 0%, #6b46c1 100%)",
                boxShadow: "0 6px 20px rgba(102, 126, 234, 0.6)",
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: "linear-gradient(135deg, #a0aec0 0%, #718096 100%)",
                boxShadow: "none",
                transform: "none",
              },
            }}
          >
            <SendIcon />
          </IconButton>
        </Box>
      </Box>
    </Box>
  );
};
